export const VEHICLE_TITLE_TRANSFER_AUTHORITY_TEMPLATE = `# Transfer vehicle and boat titles for {{deceased_first_name}}

{{requestor_first_name}}, this guide walks you through transferring {{deceased_first_name}}'s vehicle title in {{vehicle_state}}.

Since you've already got your legal authority documents ready, let's walk through the final steps of getting this title transferred. It's a bit of paperwork, but we'll take it one piece at a time.

The steps can feel a little technical, but the process usually comes down to three things:
- Gather the vehicle title, death certificate, and your legal authority document
- Complete the correct state form
- Submit everything to the correct DMV, title office, county office, or tag office

# Defining Your Authority

The guide repeatedly uses the phrase "legal authority document" without explaining what that actually is. While you may have a Will, the title office typically requires a court-issued document to confirm your legal authority. Common documents used to establish authority include the following, issued by a personal representative: Letters Testamentary, Letters of Administration, or a Small Estate Affidavit.

# Before You Start

Before you sign or submit anything:
- Check the vehicle title for a lien or loan.
- Check with the insurance provider, as the estate typically maintains the existing policy to protect the asset until the transfer is complete.
- Obtain a separate insurance policy in the new owner's name before requesting a registration transfer.

**Decision Point:** If you are not driving the vehicle immediately (e.g., if it will be sold or parked), you can often apply for Title Only to establish ownership. If you intend to drive it, you must apply for Registration and Title.

DO NOT sign any form that has a 'Notary' or 'Officer' signature line until you are standing in front of that official.

It might feel instinctual to sign their name, but you'll actually be signing your own name as the person authorized to handle the estate. The DMV requires verification that the person signing has the legal authority to represent the estate.

Review the vehicle title for co-owners or trust designations. If the title lists a co-owner, a trust, or a 'Transfer on Death' (TOD) beneficiary, contact a legal professional or the DMV to confirm the specific requirements for those ownership types.

# What to Bring

- [ ] Original vehicle title
- [ ] Certified death certificate (with the official raised seal)
- [ ] Your government-issued photo ID
- [ ] Your legal authority or state-approved transfer document
- [ ] Completed state title form
- [ ] Current odometer reading
- [ ] Proof of insurance, if the vehicle will be registered
- [ ] An official Lien Release letter or a signed-off title from the financial institution; a simple bank statement is usually insufficient.
- [ ] Payment for title, registration, tax, and local fees

# Where to Go

{{#if vehicle_state_CA}}
**California**

| What | Details |
| --- | --- |
| Best path | Visit a California DMV field office. California also allows some title transfer documents to be mailed. |
| Official page | California DMV title transfers |
| Appointment link | [Make a DMV appointment](https://www.dmv.ca.gov/portal/appointments/) |
| Phone | 1-800-777-0133 |
| Mail | DMV, PO Box 942869, Sacramento, CA 94269 |
| Fee note | Fees start at $15 for the base title transfer fee, plus the variable 'Transportation Improvement Fee' which depends on vehicle value. Use the DMV fee calculator or confirm with DMV. |
{{/if}}
{{#if vehicle_state_TX}}
**Texas**

| What | Details |
| --- | --- |
| Best path | Visit the County Tax Assessor-Collector's office in the county where the new owner resides or where the vehicle will be garaged. |
| Official page | Texas vehicle title transfer FAQ |
| Office locator | [Find your county tax office](https://www.txdmv.gov/regional-service-centers/county-tax-offices) |
| Phone | Local County Tax Assessor-Collector (state number 1-888-368-4689 is for secondary backup). |
| Mail | Use your county office's instructions. Texas does not list one statewide mailing address for this transfer. |
| Fee note | Fees vary by county and transaction type. Confirm with the county tax office before you go. |
{{/if}}
{{#if vehicle_state_NY}}
**New York**

| What | Details |
| --- | --- |
| Best path | Visit a New York DMV office. Some title paperwork can be mailed if the DMV checklist allows it. |
| Official checklist | MV-843, Transfer of Vehicle Registered in Name of Deceased Person |
| Office locator | [Find a New York DMV office](https://dmv.ny.gov/offices) |
| Phone | Downstate: 1-800-698-2931. Upstate: 1-518-486-9786. |
| Mail | Follow the mailing instructions on MV-843. For duplicate title requests, the Title Bureau address is NYS DMV Title Bureau, PO Box 2750, Albany, NY 12220-0750. |
| Fee note | The base title fee is $50. Residents in the 12-county downstate region (including NYC) should expect an additional Metropolitan Commuter Transportation District (MCTD) fee of $25 or more. |
{{/if}}
{{#if vehicle_state_FL}}
**Florida**

| What | Details |
| --- | --- |
| Best path | Visit the local county tax collector office or license plate agency in the county where the new owner resides or where the vehicle will be garaged. |
| Official form | HSMV 82040 MV, Application for Certificate of Motor Vehicle Title |
| Office locator | [Find a Florida tax collector or license plate agency](https://flhsmv.gov/locations/) |
| Phone | 850-617-2000 |
| Online contact | FLHSMV contact form |
| Mail | Use your local county tax collector's instructions. Florida does not list one statewide mailing address for this transfer. |
| Fee note | Fees vary by county, registration choice, and title status. Confirm with the tax collector office. |
{{/if}}
{{#if vehicle_state_IL}}
**Illinois**

| What | Details |
| --- | --- |
| Best path | Visit an Illinois Secretary of State facility. |
| Official page | VSD 190, Application for Vehicle Transaction(s) (PDF) |
| Office locator | [Find an Illinois facility](https://apps.ilsos.gov/facilityfinder/facility) |
| Phone | 800-252-8980 in Illinois, 217-785-3000 outside Illinois |
| Mail | Use Secretary of State instructions if they ask you to mail documents. |
| Fee note | Illinois lists a $165 title fee or a $50 Corrected Title fee depending on how the clerk codes the transaction, plus registration fees and RUT-50 tax compliance when applicable. |
{{/if}}
{{#if vehicle_state_PA}}
**Pennsylvania**

| What | Details |
| --- | --- |
| Best path | Visit a PennDOT authorized title and registration agent. |
| Official page | Vehicle Transfer after Death of Owner fact sheet |
| Form | MV-39, Notification of Assignment/Correction of Vehicle Title Upon Death of Owner |
| Phone | 717-412-5300 |
| Mail | Use the address and instructions on the PennDOT form or the title agent's instructions. |
| Fee note | Current PennDOT fee schedule lists $72 for a certificate of title and $11 for transfer of registration plate, when applicable. |
{{/if}}
{{#if vehicle_state_OH}}
**Ohio**

| What | Details |
| --- | --- |
| Best path | Visit a County Clerk of Courts Title Office in the county where the new owner resides or where the vehicle will be garaged. Do not go to a regular driver-license office for title transfer. |
| Official page | Ohio BMV transfer on death and deceased owner title guidance |
| Office locator | [Find an Ohio title office](https://www.bmv.ohio.gov/locations.aspx) |
| Phone | Contact your specific County Clerk of Courts Title Office (general state number is 614-752-7671). |
| Email/contact | Use Ohio BMV "Ask Titles" from the official title page. |
| Mail | Use your County Clerk of Courts Title Office instructions. |
| Fee note | Title fees can vary by county. Confirm with the title office before you go. |
{{/if}}
{{#if vehicle_state_GA}}
**Georgia**

| What | Details |
| --- | --- |
| Best path | Visit the County Tag Office in the county where the new owner resides or where the vehicle will be garaged. |
| Official page | Vehicle inherited or purchased from an estate |
| Office locator | [Find a county tag office](https://dor.georgia.gov/county-tag-offices) |
| Phone | Georgia Call Center: 1-800-436-7442 |
| Mail | Use your county tag office's instructions. Georgia does not list one statewide mailing address for this transfer. |
| Fee note | Fees and taxes depend on county, vehicle, and transfer type. Confirm with the county tag office. |
{{/if}}
{{#if vehicle_state_NC}}
**North Carolina**

| What | Details |
| --- | --- |
| Best path | Visit an NCDMV License Plate Agency. |
| Official page | NCDMV title documents and forms |
| Forms page | NCDMV forms |
| Phone | 919-715-7000 |
| Mail | Use NCDMV mailing instructions only if NCDMV tells you to mail. General mailing address: 3101 Mail Service Center, Raleigh, NC 27699-3101. |
| Fee note | Fees vary by title, registration, plate, and tax status. Confirm with the license plate agency. |
{{/if}}
{{#if vehicle_state_MA}}
**Massachusetts**

| What | Details |
| --- | --- |
| Best path | Visit an RMV Service Center. |
| Official form | Registration and Title Application |
| Tax exemption form | MVU-27, estate or inheritance tax exemption affidavit |
| Office locator | [Find an RMV Service Center](https://www.mass.gov/rmv-service-center-702702) |
| Phone | 857-368-8000 |
| Mail | Use RMV instructions if they tell you to mail. For most title transfers, plan on visiting an RMV Service Center. |
| Fee note | Fees depend on title, registration, plate, and tax exemption status. Confirm with RMV. |
{{/if}}
{{#if vehicle_state_WA}}
**Washington**

| What | Details |
| --- | --- |
| Best path | Visit or mail documents to a Vehicle Licensing Office. |
| Official page | Washington DOL vehicle title transfer |
| Office locator | [Find a Vehicle Licensing Office](https://www.dol.wa.gov/about/vehicle-licensing-offices.html) |
| Phone | 360-902-3770 |
| Mail | Send completed applications to a Vehicle Licensing Office address from the official locator. |
| Fee note | Fees vary by location and transaction. Contact the Vehicle Licensing Office for the total before submitting. |
{{/if}}
{{#if vehicle_state_VA}}
**Virginia**

| What | Details |
| --- | --- |
| Best path | Visit a Virginia DMV Customer Service Center. |
| Official page | Virginia DMV transfer ownership after death |
| Form | VSA 17A, Application for Certificate of Title and Registration |
| Phone | 804-497-7100 |
| Mail | Virginia DMV, PO Box 27412, Richmond, VA 23269, if DMV instructions say mailing is allowed for your form. |
| Fee note | Fees depend on title, registration, and plate choices. Confirm with DMV before submitting. |
{{/if}}
{{#if vehicle_state_NJ}}
**New Jersey**

| What | Details |
| --- | --- |
| Best path | Visit a New Jersey MVC Vehicle Center. |
| Official page | NJ MVC transferring vehicle ownership |
| Office locator | [Find an MVC location](https://www.nj.gov/mvc/locations/) |
| Phone | 609-292-6500 for registrations and titles |
| Mail | NJ MVC requires these documents to be brought to a Vehicle Center for this transaction. |
| Fee note | NJ MVC lists a $60 title fee, or $85 if the vehicle is financed. |

**Critical note:** If you're putting the title in the estate's name, New Jersey requires the estate's tax ID number (often called an EIN). Just make sure you have that handy.
{{/if}}
{{#if vehicle_state_MI}}
**Michigan**

| What | Details |
| --- | --- |
| Best path | Visit a Michigan Secretary of State office. |
| Official page | Michigan title transfer and vehicle registration |
| Office locator | [Schedule an office visit](https://www.michigan.gov/sos/office-locator) |
| Phone | 888-767-6424 |
| Mail | Michigan SOS lists office visit as the path for this type of title transfer. |
| Fee note | Michigan lists a $15 title transfer fee. Additional registration or payment-card fees may apply. |
{{/if}}
{{#if vehicle_state_CO}}
**Colorado**

| What | Details |
| --- | --- |
| Best path | Visit your county motor vehicle office. |
| Official page | Colorado county motor vehicle offices |
| Form | DR 2395, Application for Title and/or Registration |
| Phone | Colorado Title and Registration: 303-205-5608 |
| Mail | Use your county motor vehicle office's instructions. Colorado does not list one statewide mailing address for county title transfers. |
| Fee note | Fees vary by county and transaction. Confirm with the county motor vehicle office. |
{{/if}}
{{#if vehicle_state_MD}}
**Maryland**

| What | Details |
| --- | --- |
| Best path | Visit an MVA branch office or licensed tag and title service. |
| Official page | Handling a loved one's vehicle and ID after death |
| Form | VR-005, Application for Certificate of Title |
| Phone | 410-768-7000 |
| Mail | Use MVA or tag and title service instructions if they tell you to mail. |
| Fee note | Fees and taxes depend on the transfer type. Maryland says excise tax is not due for certain transfers to a surviving spouse, legal heir, legatee, or distributee. |
{{/if}}
{{#if vehicle_state_CT}}
**Connecticut**

| What | Details |
| --- | --- |
| Best path | Visit a Connecticut DMV hub or branch office. |
| Official page | Transfer vehicle ownership |
| Form | H-13B, Application for Registration and Title |
| Phone | Title questions: 860-263-5710 |
| Mail | Use DMV instructions if they tell you to mail. For title transfer, plan on an appointment at a DMV office. |
| Fee note | Fees depend on title, registration, lien, and tax status. Confirm with the DMV before submitting. |
{{/if}}
{{#if vehicle_state_has_guide}}{{else}}
Because every state has its own quirks, a quick call to the local title office in {{vehicle_state}} is the best way to make sure we have exactly what they're looking for before you head out.

| What | Details |
| --- | --- |
| How to find the office | Search online for "{{vehicle_state}} vehicle title transfer after death official" or "{{vehicle_state}} DMV deceased owner vehicle title transfer." |
| Official site to use | Use an official state or local government website when possible. Look for a .gov website or the official state motor vehicle agency. |
| Phone | Use the phone number listed on the official state motor vehicle or title office website. |
| Mail | Only mail documents if the official office confirms that mailing is allowed for this transfer. |
| Fee note | Fees vary by state and county. Ask the office to confirm the title fee, registration fee, tax, and accepted payment methods before you submit documents. |

**Critical Inquiry:** When you call, ask the office if a "Release of Interest" is required from all other legal heirs to prevent multiple trips.
{{/if}}

# Phone Script

Use this if you want to confirm the process before going in:

"Hi, I need to transfer a vehicle title for someone who has passed away. The vehicle is currently under the name of {{deceased_full_name}}. Could you tell me what documents I need to bring to your office to process this?"

# Mail Cover Note

Use this only if the official office confirms that mailing is allowed.

**Subject: Vehicle title transfer after death**

Hello,

I am submitting documents to transfer the vehicle title after the death of {{deceased_full_name}}.

Enclosed are:
- Completed title transfer form
- Original vehicle title, if required
- Certified copy of the death certificate
- Copy of my legal authority document
- Copy of my government-issued photo ID, if required
- Lien release, if applicable
- Payment for applicable fees

Vehicle information:
- Year: {{vehicle_year}}
- Make: {{vehicle_make}}
- Model: {{vehicle_model}}
- VIN: {{vehicle_VIN}}
- Plate number: {{vehicle_plate_number}}

Please process the title transfer or contact me if anything else is needed.

Thank you,
{{user_full_name}}

# Form and Cheat Sheet

{{#if vehicle_state_CA}}
## California Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| REG 256, Statement of Facts | Estate, inheritance, court order, use tax exemption, or other DMV explanation |
| REG 227, Application for Replacement or Transfer of Title | Only if the California title is lost, stolen, mutilated, illegible, or not available |

**Cheat Sheet: REG 256, Statement of Facts**

| Form section / field | What to enter |
| --- | --- |
| License Plate/CF Number | {{vehicle_plate_number}} |
| Vehicle/Vessel ID Number | {{vehicle_VIN}} |
| Year/Make | {{vehicle_year}} {{vehicle_make}} |
| Section A: Statement for Use Tax Exemption | In Section A, consult the DMV clerk or a tax professional to determine if the transfer qualifies for a 'Gift' or 'Decedent' tax exemption |
| Section G: Statement of Facts | Write that {{deceased_full_name}} passed away and that {{user_full_name}} is submitting the title transfer with the required estate authority document |
| Section H: Applicant's Signature | {{user_full_name}} |
| Printed Last Name / First Name / Middle Name | {{user_full_name}} |
| Daytime Phone Number | {{user_phone_number}} |
| Date | Today's date |

**Cheat Sheet: REG 227, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Vehicle License Plate Number | {{vehicle_plate_number}} |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year/Make of Vehicle | {{vehicle_year}} {{vehicle_make}} |
| Section 1: Registered Owner(s) of Record | {{deceased_full_name}}, as shown on DMV records |
| Section 3: Missing Title Statement | Check the reason the title is not available |
| Section 6: New Registered Owner(s) | {{user_full_name}} and your address |
| Signature(s) of All New Owner(s) | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_TX}}
## Texas Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| Form 130-U, Application for Texas Title and/or Registration | Main Texas title and registration application |
| Form VTR-262, Affidavit of Heirship for a Motor Vehicle | Only if the estate is not probated and Texas allows an heirship transfer |

**Cheat Sheet: Form 130-U**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Body Style | {{vehicle_body_type}} |
| Model | {{vehicle_model}} |
| Odometer Reading | Current mileage |
| Applicant Type | Individual, unless the county office tells you otherwise |
| Applicant Name | {{user_full_name}} |
| Applicant Mailing Address | Your mailing address |
| Previous Owner Name | {{deceased_full_name}} |
| Sales Price | Verify with the county office if the transfer qualifies as a 'Gift'; otherwise, follow their instructions for the sales price section |
| Signature of Applicant/Owner | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: VTR-262, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Body Style | {{vehicle_body_type}} |
| Model | {{vehicle_model}} |
| License Plate Number | {{vehicle_plate_number}} |
| Recorded Owner Name | {{deceased_full_name}} |
| Date of Death | {{date_of_death}} |
| Heir Name | {{user_full_name}} |
| Heir Address | Your mailing address |
| Transferee Name | {{user_full_name}} |
| Odometer Disclosure Statement | Current mileage |
| Signature | Sign in front of a notary, if required by the form |
{{/if}}
{{#if vehicle_state_NY}}
## New York Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| MV-843, Transfer of Ownership When the Vehicle Owner is Deceased | Procedural checklist for attachments (e.g., death certificate, letters testamentary). |
| MV-82, Vehicle Registration/Title Application | If registering and titling the vehicle |
| MV-82TON, Application for Title | If applying for title only, not registration |
| DTF-802, Statement of Transaction | Tax statement for sale or gift transfer |
| DTF-803, Claim for Sales and Use Tax Exemption | If claiming a sales/use tax exemption |

**Cheat Sheet: MV-843**

| Form section / field | What to do |
| --- | --- |
| MV-82 or MV-82TON | Check that the correct title/registration form is completed |
| Proof of Name and Date of Birth | Attach your/new owner's government-issued photo ID |
| Proof of Ownership | Attach the title or accepted proof listed on the checklist |
| Proof of Authority | Attach Letters Testamentary, Letters of Administration, voluntary administrator affidavit, or other accepted authority |
| Sales Tax Form | Attach DTF-802 or DTF-803, depending on the transfer |
| Fee | Include the title fee and any registration/plate fees DMV requires |

**Cheat Sheet: MV-82**

| Form section / field | What to enter |
| --- | --- |
| I Want To | Check Title, or Registration and Title |
| Registrant Name | {{user_full_name}} |
| Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Type | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: DTF-802 or DTF-803**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Information | {{vehicle_VIN}}, {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}} |
| New Owner Information | {{user_full_name}} and address |
| Previous Owner Information | {{deceased_full_name}} |
| Transaction Type / Exemption | Select the option that matches inheritance, gift, or tax exemption, if accepted |
| Signature | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_FL}}
## Florida Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| HSMV 82040 MV, Application for Certificate of Motor Vehicle Title | Main Florida motor vehicle title application |
| HSMV 82152, Application for Surviving Spouse Transfer | Only for surviving spouse title transfer |

**Cheat Sheet: HSMV 82040 MV**

| Form section / field | What to enter |
| --- | --- |
| Section 1: Applicant Information | {{user_full_name}} and your address |
| Section 2: Motor Vehicle Description | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}}, {{vehicle_body_type}}, {{vehicle_VIN}} |
| Title Number | Current Florida title number, if available |
| Section 5: Transfer Type | Select "Inheritance," if available and accurate |
| Section 6: Odometer Declaration | Current mileage |
| Section 7: Certificate of Title Type | Select the title type requested |
| Section 11: Lienholder Information | Complete only if a lien remains |
| Section 12: Application Attestment and Signatures | {{user_full_name}} |
| Date | Today's date |

{{#if is_spouse}}
**Cheat Sheet: HSMV 82152, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Section 1: Owner Information | Surviving spouse's full legal name, date of birth, FL DL/ID number, your address, and email |
| Title Number | Current title number |
| Audit Control Number | From the Florida title, if available |
| Certification | Review and check the correct certification boxes |
| Signature of Owner | Surviving spouse signs |
| Date | Today's date |
{{/if}}
{{/if}}
{{#if vehicle_state_IL}}
## Illinois Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| VSD 190, Application for Vehicle Transaction(s) | Main Illinois title/registration transaction |
| RUT-50, Private Party Vehicle Use Tax Transaction Return | Main Illinois title/registration transaction (Recommended manual PDF for private citizens) |
{{#if legal_role_sea}}
| RT OPR 31, Small Estate Affidavit | Only if using Illinois small estate affidavit path |
{{/if}}

**Cheat Sheet: VSD 190**

| Form section / field | What to enter |
| --- | --- |
| Transaction Type | Corrected title, title transfer, or title and registration, depending on the office instruction |
| Owner Name | {{user_full_name}} |
| Owner Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Style | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: RUT-50**

| Form section / field | What to enter |
| --- | --- |
| Purchaser Information | {{user_full_name}} and address |
| Seller Information | Estate of {{deceased_full_name}} |
| Vehicle Information | {{vehicle_VIN}}, {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}} |
| Purchase Price | $0 or inheritance/estate transfer, if accepted |
| Tax / Exemption Lines | Follow the facility or Department of Revenue instruction |
| Signature | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_PA}}
## Pennsylvania Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| MV-39, Notification of Assignment/Correction of Vehicle Title Upon Death of Owner | Main deceased-owner transfer form |
| MV-4ST, Vehicle Sales and Use Tax Return/Application for Registration | Usually completed by an authorized PennDOT agent |
| Vehicle Transfer after Death of Owner fact sheet | Official instruction sheet for death transfer situations |

**Cheat Sheet: MV-39**

| Form section / field | What to enter |
| --- | --- |
| Title Number | Current Pennsylvania title number |
| Last Name / First Name / Middle Name | {{deceased_full_name}} |
| Make of Vehicle | {{vehicle_make}} |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Registration Plate Number | {{vehicle_plate_number}} |
| Date of Death | {{date_of_death}} |
| New Owner Name | {{user_full_name}} |
| Street Address / City / State / Zip | New owner's address |
| Certification / Signature | Sign ONLY in the presence of a notary public; the back of the original title requires a notarized signature from the legal representative of the estate. |
| Notarization | Complete if the specific section requires notarization |

**Cheat Sheet: MV-4ST, handled by agent**

| Form section / field | What to prepare |
| --- | --- |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_VIN}} |
| Purchaser Information | {{user_full_name}} and address |
| Insurance Information | New owner's insurance company and policy number |
| Tax / Exemption | Agent will help complete based on estate/inheritance transfer |
| Signature | Sign where the agent instructs |
{{/if}}
{{#if vehicle_state_OH}}
## Ohio Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| BMV 3774, Application for Certificate of Title to a Motor Vehicle | Main Ohio title application |
{{#if is_spouse}}
| BMV 3773, Surviving Spouse Affidavit | Only for surviving spouse transfer |
{{/if}}

**Cheat Sheet: BMV 3774**

| Form section / field | What to enter |
| --- | --- |
| Applicant Name | {{user_full_name}} |
| Applicant Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Type | {{vehicle_body_type}} |
| Previous Owner | {{deceased_full_name}} |
| Odometer Reading | Current mileage |
| Reason for Transfer | Estate transfer or inheritance |
| Signature | {{user_full_name}} |
| Date | Today's date |

{{#if is_spouse}}
**Cheat Sheet: BMV 3773, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Court Information | County Court of Common Pleas information |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_body_type}}, {{vehicle_VIN}} |
| Value | Fair market value of the vehicle |
| Surviving Spouse Name | {{user_full_name}} |
| Attestation | Confirm the statements that apply |
| Signature | Sign where the Clerk of Courts instructs |
{{/if}}
{{/if}}
{{#if vehicle_state_GA}}
## Georgia Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| MV-1, Motor Vehicle Title/Tag Application | Main Georgia title/tag application |
| T-20, Affidavit of Inheritance | Inheritance affidavit transfer, if required |

**Cheat Sheet: MV-1**

| Form section / field | What to enter |
| --- | --- |
| Owner's Full Legal Name | {{user_full_name}} |
| Owner's Address | Your address |
| County of Residence | New owner's Georgia county |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Style | {{vehicle_body_type}} |
| Current Title Number | Current title number, if available |
| Security Interest or Lienholder | Complete only if a lien remains |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: T-20, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Decedent's Full Legal Name | {{deceased_full_name}} |
| Date of Death | {{date_of_death}} |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}}, {{vehicle_VIN}} |
| Current Title Information | Georgia title number, if available |
| Heir / Applicant Information | {{user_full_name}} and address |
| Lien / Security Interest Information | Complete if shown on the title |
| Signature | Sign in front of a notary, if required |
{{/if}}
{{#if vehicle_state_NC}}
## North Carolina Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| MVR-1, Title Application | Main North Carolina title application |
| MVR-180, Odometer Disclosure Statement | If odometer disclosure is required |
| MVR-181, Damage Disclosure Statement | If damage disclosure is required |

**Cheat Sheet: MVR-1**

| Form section / field | What to enter |
| --- | --- |
| Owner 1 ID Number | New owner's NC driver license or ID number, if applicable |
| Owner 1 Full Legal Name | {{user_full_name}} |
| Residence Address | Your address |
| Mailing Address | Your mailing address, if different |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Body Style | {{vehicle_body_type}} |
| Fuel Type | Vehicle fuel type |
| Odometer Reading | Current mileage |
| Lienholder Information | Complete only if a lien remains |
| Insurance Company / Policy Number | Complete if registering |
| Owner's Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: MVR-180 / MVR-181, if required**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year / Make / Model | {{vehicle_year}} {{vehicle_make}} {{vehicle_model}} |
| Odometer Reading | Current mileage |
| Damage Disclosure | Answer based on the vehicle's known history |
| Signature | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_MA}}
## Massachusetts Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| Registration and Title Application | Main Massachusetts title/registration application |
| MVU-27, Affidavit in Support of a Claim for Exemption from Sales or Use Tax | Estate, will, intestacy, or inheritance tax exemption |
{{#if is_spouse}}
| Affidavit of Surviving Spouse | Only for surviving spouse transfer |
{{/if}}

**Cheat Sheet: Registration and Title Application**

| Form section / field | What to enter |
| --- | --- |
| Registration Type | Title only, or title and registration |
| Owner Information | {{user_full_name}} |
| Residential Address | Your address |
| Mailing Address | Your mailing address, if different |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Style | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Insurance Certification | Have insurance agent complete/stamp if registering |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: MVU-27**

| Form section / field | What to enter |
| --- | --- |
| Transferee | {{user_full_name}} |
| Transferor / Decedent | {{deceased_full_name}} |
| Vehicle Information | {{vehicle_VIN}}, {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}} |
| Basis for Exemption | Transfer by intestacy, will, or otherwise, as applicable |
| Signature | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_WA}}
## Washington Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| TD-420-001, Vehicle Title Application | Main Washington title application |
| TD-420-041, Affidavit of Inheritance/Litigation | Inheritance or court/litigation transfer support |

**Cheat Sheet: TD-420-001**

| Form section / field | What to enter |
| --- | --- |
| Registered Owner | {{user_full_name}} |
| Residence Address | Your address |
| Mailing Address | Your mailing address, if different |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Style | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Legal Owner / Lienholder | Complete only if a lien remains |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: TD-420-041, if required**

| Form section / field | What to enter |
| --- | --- |
| License Plate / Registration Number | {{vehicle_plate_number}} |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year / Make / Model / Body Style | {{vehicle_year}} {{vehicle_make}} {{vehicle_model}} {{vehicle_body_type}} |
| Name of Deceased | {{deceased_full_name}} |
| Date of Death | {{date_of_death}} |
| Inheritance section | Complete if no executor/administrator was appointed |
| Litigation section | Complete if the transfer is based on court/probate order |
| Signature | {{user_full_name}} |
| Date | Today's date |

Verify if other legal heirs are involved in the transfer. Attach a Release of Interest (Form TD-420-050) for any legal heir not becoming a new owner.
{{/if}}
{{#if vehicle_state_VA}}
## Virginia Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| VSA 17A, Application for Certificate of Title and Registration | Main Virginia title/registration application |
| VSA 24, Transfer Virginia Title Certification | Certain deceased-owner transfer situations, if DMV requires it |

**Cheat Sheet: VSA 17A**

| Form section / field | What to enter |
| --- | --- |
| Application Type | Title, or title and registration |
| Owner's Full Legal Name | {{user_full_name}} |
| Residence Address | Your address |
| Mailing Address | Your mailing address, if different |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Type | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Title Number | Current title number, if available |
| Lien Information | Complete only if a lien remains |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: VSA 24, if required**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}}, {{vehicle_VIN}} |
| Deceased Owner | {{deceased_full_name}} |
| Date of Death | {{date_of_death}} |
| Person Authorized to Assign Title | {{user_full_name}} |
| Authority / Certification | Complete based on DMV instruction |
| Signature | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_NJ}}
## New Jersey Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| OS/SS-UTA, Universal Title Application | New Jersey title application, if required |
| NJ MVC ownership transfer page | Official estate/deceased-owner instructions |

**Cheat Sheet: OS/SS-UTA**

| Form section / field | What to enter |
| --- | --- |
| Applicant Name | {{user_full_name}} |
| Applicant Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Type | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Lienholder Information | Complete only if a lien remains |
| Signature of Applicant | {{user_full_name}} |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_MI}}
## Michigan Form and Cheat Sheet

| Form / document | Use this for |
| --- | --- |
| Michigan title transfer guidance | Official deceased-owner title transfer steps |
| TR-40, Transferring vehicle ownership of a deceased family member | Only for non-probated family/next-of-kin transfers |

**Cheat Sheet: Michigan Title Assignment**

| Title / office field | What to prepare |
| --- | --- |
| Seller / Transferor | Estate of {{deceased_full_name}}, signed by the personal representative where required |
| Purchaser / New Owner Name | {{user_full_name}} |
| New Owner Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Odometer Reading | Current mileage |
| Letter of Authority | Bring a copy with the signed title |
| Proof of Michigan No-Fault Insurance | Bring if registering the vehicle |
| License or ID | Bring user's valid ID |
{{/if}}
{{#if vehicle_state_CO}}
## Colorado Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| DR 2395, Application for Title and/or Registration | Main Colorado title/registration application |
{{#if legal_role_sea}}
| DR 2712, Affidavit for Collection of Personal Property | Only if using Colorado successor/small-estate affidavit path |
{{/if}}

**Cheat Sheet: DR 2395**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body | {{vehicle_body_type}} |
| Fuel Type | Vehicle fuel type |
| Odometer Reading and Indicator | Current mileage |
| Owner Name | {{user_full_name}} |
| Mailing Address | Your mailing address, if different |
| First Lienholder Name and Address | Complete only if a lien remains |
| Insurance Statement | Complete if registering |
| Signature | {{user_full_name}} |
| Date | Today's date |

{{#if legal_role_sea}}
**Cheat Sheet: DR 2712, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Decedent Name | {{deceased_full_name}} |
| Vehicle Year / Make / Model | {{vehicle_year}} {{vehicle_make}} {{vehicle_model}} |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Successor Name | {{user_full_name}} |
| Successor Address | Your/new owner's current address |
| Required Statement | Confirm the estate qualifies and no personal representative has been appointed, if true |
| Signature | Sign in front of a notary, if required |
| Date | Today's date |
{{/if}}
{{/if}}
{{#if vehicle_state_MD}}
## Maryland Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| VR-005, Application for Certificate of Title | Main Maryland title application |
| VR-151, You've Inherited a Vehicle | Inherited-vehicle instructions and support document |

**Cheat Sheet: VR-005**

| Form section / field | What to enter |
| --- | --- |
| Name of Owner | {{user_full_name}} |
| Address | Your address |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Body Style | {{vehicle_body_type}} |
| Odometer Mileage | Current mileage |
| Insurance Information | New owner's Maryland insurance details, if registering |
| Lien Information | Complete only if a lien remains |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: VR-151**

| Form section / field | What to prepare |
| --- | --- |
| Title | Current vehicle title |
| Death Proof | Certified death certificate or MVA notice letter, if applicable |
| Lien Release | Required if the title shows a paid-off lien |
| Assignment of Ownership | Complete only if selling or giving the vehicle to another person |
| Buyer / New Owner Name | {{user_full_name}}, if you are taking ownership |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}}, {{vehicle_VIN}} |
{{/if}}
{{#if vehicle_state_CT}}
## Connecticut Form and Cheat Sheet

| Form | Use this for |
| --- | --- |
| H-13B, Application for Registration and Title | Main Connecticut registration/title application |
| Q-1, Supplemental Assignment of Ownership | Only if the vehicle does not require a title and DMV asks for supplemental assignment |
| Connecticut transfer ownership page | Official deceased family member transfer instructions |

**Cheat Sheet: H-13B**

| Form section / field | What to enter |
| --- | --- |
| Registration Type | Title only, or registration and title |
| Owner Name | {{user_full_name}} |
| Owner Address | Your address |
| Mailing Address | Your mailing address, if different |
| Vehicle Identification Number | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body Style | {{vehicle_body_type}} |
| Odometer Reading | Current mileage |
| Security Interest / Loan or Lien | Complete only if a lien remains |
| Section 5: Sales Tax Exemption | Complete only if DMV confirms the exemption applies |
| Signature | {{user_full_name}} |
| Date | Today's date |

**Cheat Sheet: Q-1, only if needed**

| Form section / field | What to enter |
| --- | --- |
| Seller / Transferor | Estate of {{deceased_full_name}}, signed by executor/administrator where required |
| Buyer / Transferee | {{user_full_name}} |
| Vehicle Information | {{vehicle_year}}, {{vehicle_make}}, {{vehicle_model}}, {{vehicle_VIN}} |
| Registration Certificate | Attach the most recent registration certificate |
| Signature | Sign where DMV instructs |
| Date | Today's date |
{{/if}}
{{#if vehicle_state_has_guide}}{{else}}
## General Form Guidance

Because vehicle title rules are different in every state, this section helps you prepare before contacting the title office in {{vehicle_state}}.

Ask the official title office which form they require for a vehicle title transfer after death. The form may be called a title application, transfer application, inheritance affidavit, estate affidavit, or another state-specific name.

| Form / document | Where to get it | Use this for |
| --- | --- | --- |
| State title transfer form | Official {{vehicle_state}} motor vehicle or title office website | Main form to transfer the title into the new owner's name |
| Estate authority document | Court, trust records, or approved estate document | Shows that you are allowed to act for the estate |
| Additional affidavit, if required | Official title office or court website | Used only if the office requires an inheritance, heirship, small estate, or supporting affidavit |
| Tax or fee form, if required | Official state tax or motor vehicle website | Used if the state needs a tax statement or exemption claim |

**Cheat Sheet: General Title Transfer Form**

| Form section / field | What to enter |
| --- | --- |
| Vehicle Identification Number / VIN | {{vehicle_VIN}} |
| Year | {{vehicle_year}} |
| Make | {{vehicle_make}} |
| Model | {{vehicle_model}} |
| Body style | {{vehicle_body_type}} |
| Plate number | {{vehicle_plate_number}} |
| Current owner / seller / transferor | {{deceased_full_name}} or Estate of {{deceased_full_name}}, depending on the office's instruction |
| New owner / buyer / transferee | {{user_full_name}} |
| Address | Your current address |
| Odometer reading | Current mileage from the vehicle |
| Date of death | {{date_of_death}} |
| Authority document | Your legal authority or state-approved transfer document |
| Lienholder section | Complete only if a lien or loan remains |
| Signature | Sign only where the title office tells you to sign |
| Date | Today's date |

# Quick Checklist

- [ ] Confirm the vehicle is titled in {{vehicle_state}}
- [ ] Find the original vehicle title
- [ ] Check the title for a lien, co-owner, TOD beneficiary, trust, or survivorship wording
- [ ] Gather the certified death certificate
- [ ] Gather your government-issued photo ID
- [ ] Gather your legal authority or state-approved transfer document
- [ ] Verify that the new owner's insurance has been transmitted to the state's database (where applicable) at least 24-48 hours before your visit.
- [ ] Complete the required state title form
- [ ] Confirm whether an appointment is needed
- [ ] Bring payment for title, registration, tax, and local fees
- [ ] Submit everything to the correct office
- [ ] Create a digital or physical copy of all signed documents for your records.
- [ ] Update insurance and registration after the transfer is complete
{{/if}}

---

*This guide is provided for informational purposes only and does not constitute legal advice. State requirements may change and can vary based on your specific situation, including the type of estate, number of heirs, and local county requirements. We recommend calling ahead to your local title office to confirm current requirements and fees. Elayne is not responsible for any errors or changes in state requirements.*`;
